import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/styles";
import { Checkbox, Divider, FormControlLabel, Snackbar } from "@material-ui/core";
import { ReflexContainer, ReflexElement, ReflexSplitter } from "react-reflex";
import { Alert } from "@material-ui/lab";

import uiConf from "../../config/uiConf";

import apiClient from "../../lib/common/apiClient";
import checkValidation from "../../lib/utils/checkValidation";
import checkConditionWithMsg from "../../lib/utils/checkConditionWithMsg";

import Dialog from "../dialog/functional/Dialog";
import DialogHeader from "../dialog/functional/DialogHeader";
import DialogContent from "../dialog/functional/DialogContent";
import DialogTitle from "../dialog/functional/DialogTitle";
import DialogActionsOkCancel from "../dialog/functional/DialogActionsOkCancel";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";

import AutoForm from "../form/functional/AutoForm";
import { debounce } from 'lodash';
import failureKey from "../../lib/common/failureKey";

const useStyles = makeStyles((theme) => ({
    keepOpenSwitch: {
        marginLeft: "8px",
    },

    label: {
        fontSize: "smaller",
        color: theme.palette.primary.text,
    },
}));

/**
 * @COMPONENT_NAME FormCustomDialog
 * @DESCRIPTION 사용자가 입력한 데이터를 기반으로 폼을 작성하고 서버에 전송할 수 있는 커스텀 다이얼로그 컴포넌트입니다.
 *              기본적으로 다이얼로그 레이아웃 안에 AutoForm을 포함하며, 유효성 검사와 API 호출, 알림 등을 포함한 폼 전송 로직을 처리합니다.
 *
 * @PROPS
 *    - {string} id - 다이얼로그 ID
 *    - {string} mode - 폼 모드 (예: "edit", "create")
 *    - {object} schema - AutoForm에 전달될 스키마 정보
 *    - {object} refresh - 데이터 새로고침 함수를 담은 객체
 *    - {string} title - 다이얼로그 상단에 표시할 제목
 *    - {object} initialData - 폼 초기 데이터
 *    - {string} formId - 폼 식별자
 *    - {boolean} moveable - 다이얼로그 이동 가능 여부
 *    - {boolean} resizable - 다이얼로그 크기 조절 가능 여부
 *    - {boolean} fullWidth - 다이얼로그 전체 너비 사용 여부
 *    - {Function} onInitialized - 다이얼로그 초기화 후 호출되는 콜백
 *    - {Function} onSuccess - API 호출 성공 시 실행할 콜백
 *    - {Function} onClose - 다이얼로그 닫기 시 실행할 콜백
 *    - {Function} onDialogUpdateDetail - 다이얼로그 업데이트 관련 콜백
 *    - {Function} appendCheckBox - 추가 체크박스 렌더링 함수
 *    - {object} labels - 버튼 텍스트(label) 설정 객체
 *    - {string} view - 폼 뷰 모드 (사용 여부 불명확)
 *    - {string} headerTitle - 헤더 타이틀(별도 사용 안 됨)
 *    - {Function} onModifyViewUrl - 뷰 URL 변경 콜백
 *    - {Function} customValidation - 사용자 정의 유효성 검사 함수
 *    - {string} apiUrl - API 호출 URL (없을 경우 schema.name 기반 생성)
 *    - {string} platform - 다이얼로그 UI 플랫폼 설정
 *    - {string} maxWidth - 다이얼로그 최대 너비
 *    - {string} method - API 호출 메서드 (post, put, patch 중 선택)
 *    - {Function} modifyUrlCallback - API URL을 동적으로 수정하는 함수
 *
 * @PASSTHROUGH_PROPS
 *    - {object} labels - 버튼 텍스트(label) 설정 객체
 *    - {object} initialData - 폼 초기 데이터
 *    - {string} title - 다이얼로그 상단에 표시할 제목
 *
 * @CALLBACKS
 *    - {Function} onInitialized - 다이얼로그가 초기화될 때 호출됨
 *    - {Function} onSuccess - API 요청 성공 후 결과를 전달받음
 *    - {Function} onClose - 다이얼로그를 닫을 때 호출됨
 *    - {Function} customValidation - 커스텀 유효성 검사를 수행함 (true 반환 시 유효성 실패로 간주)
 *    - {Function} modifyUrlCallback - API 요청 URL을 동적으로 변경함
 *    - {Function} appendCheckBox - 다이얼로그 하단에 렌더링할 추가 체크박스를 반환함
 *
 * @EXAMPLES
 * ```jsx
 * <FormCustomDialog
 *   id="userFormDialog"
 *   title="사용자 등록"
 *   schema={userSchema}
 *   initialData={{ name: '', age: 0 }}
 *   onSuccess={(data) => console.log("등록 성공:", data)}
 *   onClose={() => console.log("다이얼로그 닫힘")}
 *   method="post"
 * />
 * ```
 */


function FormCustomDialog(props) {
    const classes = useStyles();

    const {
        id,
        mode,
        schema,
        refresh,

        title,
        initialData,

        formId,

        moveable,
        resizable,
        fullWidth,

        onInitialized,
        onSuccess,
        onClose,

        onDialogUpdateDetail,

        appendCheckBox,

        labels,
        view,
        headerTitle,
        onModifyViewUrl,
        customValidation,
        apiUrl,
        platform,
        maxWidth,
        method,

        modifyUrlCallback,
        modifyFormDataCallback,
        modifyFormDataCallbackOK,

        checkDisabledCallback,

        ...others
    } = props;

    const [, setHeight] = useState(0);

    const [dialogInstance, setDialogInstance] = useState();
    const [okButtonDisabled, setOkButtonDisabled] = useState(true);
    const [formData, setFormData] = useState({});
    const [keepOpen, setKeepOpen] = useState(false);

    const [tableSchema, setTableSchema] = useState(schema);

    const [notification, setNotification] = useState({
        open: false,
        severity: "success",
        msg: "",
    });

    const handleResize = () => {
        setHeight(document.documentElement.offsetHeight - 96);
    };

    useEffect(() => {
        window.addEventListener("resize", handleResize);

        handleResize();

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {

        if (schema) {

            let columnMap = {};

            for (const column of schema.columns) {

                columnMap[column.name] = column;

            }

            schema.columnMap = columnMap;

        }

        setTableSchema(schema);

    }, [schema]);

    const { t } = useTranslation();

    const onCloseDialog = () => {
        if (onClose) {
            onClose();
        }
        setOkButtonDisabled(true)
        setFormData();
    };

    const onCancel = () => {
        dialogInstance.hide();
    };

    const onDialogInitialized = (instance) => {
        setDialogInstance(instance);

        if (onInitialized) {
            onInitialized(instance);
        }
    };

    const onChangeFormData = (formData) => {
        setFormData(formData);

        setOkButtonDisabled(false);
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const nofifyWarning = (msg) => {
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onApiSuccess = (result) => {
        if (onSuccess) {
            setTimeout(onSuccess(result.data), 0);
        }
        if (!keepOpen) {
            dialogInstance.hide();
        }

    };

    const onApiFailure = (reason) => {
        notifyFailure(
            t(failureKey("insert", reason.response?.status), {
                reason: reason.response ? reason.response.data : JSON.stringify(reason),
            })
        );
    };

    const checkItemValidation = (schema, formData) => {

        for (const column of schema.columns) {
            let [valid, msg] = checkValidation( mode, column, column.validation, formData[column.name] || "");

            if (!valid) {
                nofifyWarning(msg);
                return false;
            }
        }

        return true;
    };

    const onOk = (schema, formData) => {
        if(modifyFormDataCallbackOK){

            modifyFormDataCallbackOK(formData)
        }

        if (customValidation) {
            if (customValidation(schema, formData)) {
                console.error(" From Validation Failed ");

                return;
            }
        }

        if (!checkItemValidation(schema, formData)) {
            console.error(" From Validation Failed ");

            return;
        }

        if (schema.validation) {
            let [valid, msg] = checkConditionWithMsg(formData, schema.validation);

            if (!valid) {
                nofifyWarning(t("warning.formDataValidationFail", { msg: msg }));
                return;
            }
        }

        let url = `/api/${schema.name}`;

        if (modifyUrlCallback) {

            url = modifyUrlCallback(url);

        }

        if (apiUrl) {
            url = apiUrl;
        }

        const apiData = { ...formData };

        const form = new FormData();

        const json = {};
        const deleted = {};
        
        for (const formItem in apiData) {
            if (schema.columnMap && schema.columnMap[formItem] && schema.columnMap[formItem].formItemType === "file") {
                deleted[formItem] = apiData[formItem].deletedFiles;
                
                for (const file of apiData[formItem].activeFiles) {
                    if (file.type === "url") continue;
                    form.append(formItem, file.file);
                }
            } else if (apiData[formItem] !== undefined) {
                json[formItem] = apiData[formItem];
            }
        }

        form.append("deleted", JSON.stringify(deleted));
        form.append("json", JSON.stringify(json));

        switch (method?.toLowerCase()) {
            case "put":
                apiClient
                    .put(url, form, {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    })
                    .then((result) => onApiSuccess(result))
                    .catch((reason) => onApiFailure(reason));
                break;
            case "patch":
                apiClient
                    .patch(url, form, {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    })
                    .then((result) => onApiSuccess(result))
                    .catch((reason) => onApiFailure(reason));
                break;
            default:
                apiClient
                    .post(url, form, {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    })
                    .then((result) => onApiSuccess(result))
                    .catch((reason) => onApiFailure(reason));
                break;
        }

    };

    const onCloseNotification = () => {
        setNotification((notification) => {
            return { ...notification, open: false };
        });
    };

    const checkKeepOpen = () => {
        return (
            <>
                <FormControlLabel
                    control={
                        <Checkbox
                            className={classes.keepOpenSwitch}
                            checked={keepOpen}
                            color="primary"
                            onChange={() => setKeepOpen((keepOpen) => !keepOpen)}
                            name="checkKeepOpen"
                            size="small"
                        />
                    }
                    label={<span className={classes.label}>{t("term.keepDialogOpen")}</span>}
                />

                {appendCheckBox ? (
                    appendCheckBox()
                ) : (
                    <></>
                )}
                <div style={{ flexGrow: 1 }}></div>
            </>
        );
    };

    return (
        <>
            <Dialog
                id={id}
                platform={platform ? platform : uiConf.dialogPlatform}
                moveable={moveable ? moveable : uiConf.dialogMoveable}
                resizable={resizable ? resizable : uiConf.dialogResizable}
                fullWidth={fullWidth ? fullWidth : uiConf.dialogFullWidth}
                maxWidth={maxWidth ? maxWidth : uiConf.autoFormDialogMaxWidth}
                onClose={onCloseDialog}
                onInitialized={onDialogInitialized}
                {...others}
            >
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>

                <Divider />

                <DialogContent dividers>

                    <AutoForm
                        id={`${tableSchema?.name}create-form`}
                        schema={tableSchema}
                        mode={mode ? mode : "edit"}
                        initialData={initialData}
                        onChange={onChangeFormData}
                        modifyFormDataCallback={modifyFormDataCallback}
                        checkDisabledCallback={checkDisabledCallback}
                    />

                    <Snackbar
                        open={notification.open && notification.severity !== "success"}
                        autoHideDuration={uiConf.dialogNotifyAutoHideDuration}
                        onClose={onCloseNotification}
                    >
                        <Alert onClose={onCloseNotification} variant="filled" severity={notification.severity}>
                            {notification.msg}
                        </Alert>
                    </Snackbar>
                </DialogContent>

                <Divider />

                <DialogActionsOkCancel
                    firstActionComponent={checkKeepOpen}
                    labels={labels}
                    buttonDisabled={[okButtonDisabled, false]} /* [okButtonDisabled, cancelButtonDisabled] */
                    onOk={() => onOk(tableSchema, formData)}
                    onCancel={onCancel}
                ></DialogActionsOkCancel>
            </Dialog>
        </>
    );
}

export default FormCustomDialog;
